// pages/location/LocationCreate.jsx
// 📍 Complete working version with Stripe Elements

import React, { useState } from 'react';
import { 
  Card, Form, Input, Button, message, Row, Col, Alert, 
  Typography, Modal, Divider, Space, Spin 
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { 
  useCreateLocationPaymentIntentMutation,
  useFetchMyLocationsQuery
} from '../restaurant/restaruant.service';
import { useSelector, useDispatch } from 'react-redux';
import { setUser } from '../../app.slice';
import { loadStripe } from '@stripe/stripe-js';
import { 
  Elements, 
  CardElement, 
  useStripe, 
  useElements 
} from '@stripe/react-stripe-js';
import { ExclamationCircleOutlined, CreditCardOutlined } from '@ant-design/icons';
import { ADDITIONAL_LOCATION_PRICE } from "../../constant/pricing";

const { Title, Text } = Typography;

// ✅ Stripe publishable key
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 
  "pk_test_51RY3xZDw6TmwGTteTx4ZQV7JniIXXKNozyM0wleq0orXumSsMUN2cmItI3TOBSeVG5nhGIjkdZ0VZh6rvmZn0Kem006samsKyC"
);

const LOCATION_PRICE = ADDITIONAL_LOCATION_PRICE;

// ============================================
// ✅ CARD ELEMENT COMPONENT
// ============================================
const CardPaymentForm = ({ onSuccess, onError, isProcessing, setIsProcessing }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardComplete, setCardComplete] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      message.error('Stripe not initialized');
      return;
    }

    if (!cardComplete) {
      message.warning('Please complete your card details');
      return;
    }

    setIsProcessing(true);

    try {
      const cardElement = elements.getElement(CardElement);
      
      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (paymentMethodError) {
        throw new Error(paymentMethodError.message);
      }

      onSuccess(paymentMethod.id);
      
    } catch (error) {
      console.error('Payment error:', error);
      onError(error.message);
      setIsProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ 
        border: '1px solid #d9d9d9', 
        borderRadius: '8px', 
        padding: '16px',
        background: '#fafafa',
        marginBottom: '16px'
      }}>
        <CardElement 
          options={cardElementOptions}
          onChange={(e) => setCardComplete(e.complete)}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
        <Button 
          type="primary" 
          htmlType="submit"
          size="large"
          loading={isProcessing}
          disabled={!stripe || !cardComplete}
          style={{ 
            background: '#47e147', 
            borderColor: '#47e147', 
            minWidth: '200px'
          }}
        >
          <CreditCardOutlined /> Pay £{LOCATION_PRICE.toFixed(2)}
        </Button>
      </div>
    </form>
  );
};

// ============================================
// ✅ MAIN LOCATION CREATE COMPONENT
// ============================================
const LocationCreate = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  
  // ✅ RTK Query hooks
  const [createPaymentIntent] = useCreateLocationPaymentIntentMutation();
  const { refetch: refetchLocations } = useFetchMyLocationsQuery();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  
  const user = useSelector((state) => state.app.user);
  const currentLocations = user?.locationIds?.length || 0;
  const maxLocations = user?.maxLocations || 1;
  const canAddMore = currentLocations < maxLocations;

  // ✅ Handle form submit - Show confirmation modal
  const onLocationSubmit = async (values) => {
    setFormData(values);
    setShowConfirmModal(true);
  };

  // ✅ Confirm location - Create payment intent
  const handleConfirmLocation = async () => {
    setShowConfirmModal(false);
    setIsProcessing(true);

    try {
      const result = await createPaymentIntent({
        locationName: formData.name,
        locationAddress: formData.address,
        userId: user._id,
        amount: LOCATION_PRICE,
      }).unwrap();

      setPaymentIntentId(result.paymentIntentId);
      setShowPaymentModal(true);
      setIsProcessing(false);

    } catch (error) {
      console.error('Error creating payment intent:', error);
      message.error(error.data?.message || 'Failed to process payment');
      setIsProcessing(false);
    }
  };

  // ✅ Handle successful payment
  const handlePaymentSuccess = async (paymentMethodId) => {
    try {
      const stripe = await stripePromise;
      
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        paymentIntentId,
        {
          payment_method: paymentMethodId,
        }
      );

      if (error) {
        throw new Error(error.message);
      }

      message.success('Payment successful! Creating your location...');
      
      const locationCreated = await checkLocationStatus(paymentIntentId);
      
      if (locationCreated) {
        message.success('Location added successfully!');
        await refetchLocations();
        setShowPaymentModal(false);
        setIsProcessing(false);
        setTimeout(() => navigate('/home'), 1500);
      } else {
        message.warning('Location created but may take a moment to appear');
        setTimeout(() => navigate('/home'), 2000);
      }
      
    } catch (error) {
      console.error('Payment confirmation error:', error);
      message.error(error.message || 'Payment failed');
      setIsProcessing(false);
    }
  };

  // ✅ Handle payment error
  const handlePaymentError = (errorMessage) => {
    message.error(errorMessage);
    setIsProcessing(false);
  };

  // ✅ Poll to check location creation status
  const checkLocationStatus = async (paymentIntentId) => {
    let attempts = 0;
    const maxAttempts = 20; // Increased to 20 attempts (40 seconds)
    
    while (attempts < maxAttempts) {
      try {
        const response = await fetch(`/api/payment/check-location-status/${paymentIntentId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });
        
        const data = await response.json();
        
        if (data.locationCreated) {
          // ✅ Update user with actual location ID
          const updatedUser = {
            ...user,
            locationIds: [...(user?.locationIds || []), data.locationId],
            activeLocationId: data.locationId,
            restaurantId: data.locationId,
          };
          dispatch(setUser(updatedUser));
          localStorage.setItem('user', JSON.stringify(updatedUser));
          return true;
        }
        
        // Wait 2 seconds before next attempt
        await new Promise(resolve => setTimeout(resolve, 2000));
        attempts++;
        console.log(`⏳ Checking location status... Attempt ${attempts}/${maxAttempts}`);
      } catch (error) {
        console.log('Checking location status...');
      }
    }
    
    return false;
  };

  // ✅ Check if user can add location
  if (!canAddMore) {
    return (
      <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
        <Card bordered={false}>
          <Alert
            message="Location Limit Reached"
            description={
              <div>
                <p>You have reached your limit of {maxLocations} location(s).</p>
                <Button 
                  type="primary" 
                  onClick={() => navigate('/upgrade')}
                  style={{ marginTop: 8 }}
                >
                  Upgrade Plan
                </Button>
              </div>
            }
            type="warning"
            showIcon
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
      <Card bordered={false} style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>

          {/* ✅ BACK BUTTON - TOP */}
              <div style={{ marginBottom: 16 }}>
                <Button 
                  type="link" 
                  onClick={() => navigate(-1)}  // ✅ Back button
                  style={{ padding: 0, fontSize: '16px' }}
                >
                  ← Back
                </Button>
              </div>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Title level={2}>🏢 Add New Location</Title>
          <Text type="secondary">Create a new location for your business</Text>
        </div>

        <Row justify="center">
          <Col xs={24} sm={22} md={20} lg={18}>
            {/* ✅ Location Limit Info */}
            <div style={{ 
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 16px', background: '#f5f5f5', borderRadius: '8px', marginBottom: 24,
            }}>
              <span style={{ fontWeight: '500' }}>
                📍 Current Locations: {currentLocations} / {maxLocations}
              </span>
              <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
                £{LOCATION_PRICE.toFixed(2)}/month per location
              </span>
            </div>

            {/* ✅ Pricing Info */}
            <div style={{ 
              background: '#f0f7ff', 
              padding: '12px 16px', 
              borderRadius: '8px', 
              marginBottom: 24,
              border: '1px solid #d6e4ff'
            }}>
              <Text type="secondary">
                💡 <strong>Pricing:</strong> Base subscription includes 1 location. 
                Each additional location costs <strong>£{LOCATION_PRICE.toFixed(2)}/month</strong>.
                Your next payment will be updated automatically.
              </Text>
            </div>

            <Form form={form} onFinish={onLocationSubmit} layout="vertical">
              <Form.Item 
                name="name" 
                label="Restaurant/Location Name" 
                rules={[{ required: true, message: 'Please enter location name' }]}
              >
                <Input placeholder="e.g., London Branch" size="large" />
              </Form.Item>

              <Form.Item 
                name="address" 
                label="Address" 
                rules={[{ required: true, message: 'Please enter address' }]}
              >
                <Input.TextArea placeholder="Enter full address" rows={3} size="large" />
              </Form.Item>

              <div style={{ textAlign: 'center', marginTop: 24, paddingTop: 24, borderTop: '1px solid #f0f0f0' }}>
                <Space size="middle">
                  <Button size="large" onClick={() => navigate('/home')}>
                    Cancel
                  </Button>
                  <Button 
                    type="primary" 
                    htmlType="submit"
                    size="large"
                    style={{ 
                      background: '#47e147', 
                      borderColor: '#47e147', 
                      minWidth: '150px' 
                    }}
                  >
                    Add Location
                  </Button>
                </Space>
              </div>
            </Form>
          </Col>
        </Row>
      </Card>

      {/* ✅ Confirmation Modal */}
      <Modal
        title="Confirm New Location"
        open={showConfirmModal}
        onCancel={() => setShowConfirmModal(false)}
        footer={null}
        width={500}
        centered
      >
        <div style={{ padding: '16px 0' }}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <ExclamationCircleOutlined style={{ fontSize: 48, color: '#faad14' }} />
          </div>
          <Title level={4} style={{ textAlign: 'center' }}>Add New Location</Title>
          
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <Text type="secondary" style={{ display: 'block' }}>
              Additional locations cost <strong>£{LOCATION_PRICE.toFixed(2)}/month</strong>
            </Text>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>
              Your next payment will be updated automatically
            </Text>
          </div>

          <div style={{ background: '#f6ffed', padding: '16px', borderRadius: '8px', marginBottom: 24 }}>
            <Row justify="space-between" style={{ marginBottom: 8 }}>
              <Col><strong>Location:</strong></Col>
              <Col>{formData.name}</Col>
            </Row>
            <Row justify="space-between" style={{ marginBottom: 8 }}>
              <Col><strong>Address:</strong></Col>
              <Col>{formData.address}</Col>
            </Row>
            <Divider style={{ margin: '8px 0' }} />
            <Row justify="space-between">
              <Col><strong>Monthly Cost:</strong></Col>
              <Col><strong style={{ color: '#52c41a' }}>£{LOCATION_PRICE.toFixed(2)}</strong></Col>
            </Row>
            <Row justify="space-between" style={{ marginTop: 4 }}>
              <Col><Text type="secondary" style={{ fontSize: '12px' }}>
                This will be added to your next payment
              </Text></Col>
            </Row>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
            <Button size="large" onClick={() => setShowConfirmModal(false)}>
              Cancel
            </Button>
            <Button 
              type="primary" 
              size="large"
              loading={isProcessing}
              onClick={handleConfirmLocation}
              style={{ 
                background: '#47e147', 
                borderColor: '#47e147', 
                minWidth: '150px' 
              }}
            >
              Proceed to Payment
            </Button>
          </div>
        </div>
      </Modal>

      {/* ✅ Payment Modal with Stripe Elements */}
      <Modal
        title={
          <div style={{ textAlign: 'center' }}>
            <CreditCardOutlined style={{ fontSize: 24, color: '#47e147' }} />
            <span style={{ marginLeft: 8 }}>Payment Details</span>
          </div>
        }
        open={showPaymentModal}
        onCancel={() => {
          setShowPaymentModal(false);
          setIsProcessing(false);
        }}
        footer={null}
        width={500}
        centered
        closable={!isProcessing}
        maskClosable={!isProcessing}
      >
        <div style={{ padding: '16px 0' }}>
          <div style={{ 
            background: '#f0f7ff', 
            padding: '12px', 
            borderRadius: '8px', 
            marginBottom: '16px',
            textAlign: 'center'
          }}>
            <Text strong>Location: </Text>
            <Text>{formData.name}</Text>
            <br />
            <Text strong>Amount: </Text>
            <Text strong style={{ color: '#52c41a' }}>£{LOCATION_PRICE.toFixed(2)}</Text>
          </div>

          <Text type="secondary" style={{ display: 'block', marginBottom: '16px', textAlign: 'center' }}>
            Enter your card details to complete the payment
          </Text>

          <Elements stripe={stripePromise}>
            <CardPaymentForm 
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
              isProcessing={isProcessing}
              setIsProcessing={setIsProcessing}
            />
          </Elements>

          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              🔒 Your payment is secure and encrypted
            </Text>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LocationCreate;